import { useCallback, useEffect } from 'react';

/**
 * Issue #374 (follow-up): Persist and restore the window scroll position so a
 * page that navigates the user away (e.g. Profile -> Terms / Privacy /
 * Community Guideline) returns the user to the exact spot they were reading.
 *
 * Mirrors the inline pattern used in CourseReview.js but exposed as a shared
 * hook so any list-style page can opt in with a single line.
 *
 * Usage:
 *   const saveScrollPosition = useScrollRestoration('profileScrollPosition');
 *   <button onClick={() => { saveScrollPosition(); navigateAway(); }} />
 */
const useScrollRestoration = (key) => {
  useEffect(() => {
    const saved = sessionStorage.getItem(key);
    if (saved === null) {
      return;
    }
    sessionStorage.removeItem(key);

    const y = parseInt(saved, 10);
    if (Number.isNaN(y)) {
      return;
    }

    // Defer one frame so layout (e.g. async user data, ad banner) is in place
    // before we scroll, otherwise the target offset can land outside the
    // initial document height.
    requestAnimationFrame(() => {
      window.scrollTo(0, y);
    });
  }, [key]);

  const saveScrollPosition = useCallback(() => {
    sessionStorage.setItem(key, window.scrollY.toString());
  }, [key]);

  return saveScrollPosition;
};

export default useScrollRestoration;
